import type { ISODate, Meal, MealItem, NutritionTarget, WaterEntry, WeightEntry } from "./model.js";

export interface NutritionSummary { calories: number; protein: number; fat: number; carbs: number }
export interface DailySummary extends NutritionSummary {
  remainingCalories: number;
  waterGlasses: number;
  target: NutritionTarget | null;
}

const zero = (): NutritionSummary => ({ calories: 0, protein: 0, fat: 0, carbs: 0 });

export const MealSummaryCalculator = {
  calculate(items: readonly MealItem[]): NutritionSummary {
    return items.filter(x => !x.deletedAt).reduce((sum, x) => ({
      calories: sum.calories + x.calories,
      protein: sum.protein + x.protein,
      fat: sum.fat + x.fat,
      carbs: sum.carbs + x.carbs,
    }), zero());
  },
};

export const NutritionCalculator = {
  fromPer100(amountGrams: number, per100: NutritionSummary): NutritionSummary {
    if (!Number.isFinite(amountGrams) || amountGrams < 0) throw new RangeError("amountGrams must be non-negative");
    const factor = amountGrams / 100;
    return Object.fromEntries(Object.entries(per100).map(([k, v]) => [k, v * factor])) as unknown as NutritionSummary;
  },
};

export const DailySummaryCalculator = {
  calculate(items: readonly MealItem[], water: readonly WaterEntry[], target: NutritionTarget | null): DailySummary {
    const nutrition = MealSummaryCalculator.calculate(items);
    const waterGlasses = water.filter(x => !x.deletedAt).reduce((n, x) => n + x.glasses, 0);
    return { ...nutrition, waterGlasses, target, remainingCalories: (target?.dailyCalories ?? 0) - nutrition.calories };
  },
};

export const WaterCalculator = {
  total(entries: readonly WaterEntry[]): number {
    return entries.filter(x => !x.deletedAt).reduce((sum, x) => sum + x.glasses, 0);
  },
};

export const WeightProgressCalculator = {
  calculate(entries: readonly WeightEntry[]): { latestKg: number | null; changeKg: number | null } {
    const sorted = entries.filter(x => !x.deletedAt).slice().sort((a, b) => a.dateTime.localeCompare(b.dateTime));
    if (!sorted.length) return { latestKg: null, changeKg: null };
    const first = sorted[0]!; const latest = sorted.at(-1)!;
    return { latestKg: latest.weightKg, changeKg: latest.weightKg - first.weightKg };
  },
};

export const StreakCalculator = {
  calculate(activeDates: readonly ISODate[], through: ISODate): number {
    const active = new Set(activeDates);
    let cursor = new Date(`${through}T12:00:00Z`); let streak = 0;
    while (active.has(cursor.toISOString().slice(0, 10))) {
      streak++; cursor.setUTCDate(cursor.getUTCDate() - 1);
    }
    return streak;
  },
};

export function itemsForMeals(meals: readonly Meal[], items: readonly MealItem[]): MealItem[] {
  const ids = new Set(meals.filter(x => !x.deletedAt).map(x => x.id));
  return items.filter(x => ids.has(x.mealId));
}
